import { yupResolver } from "@hookform/resolvers/yup";
import { Autocomplete, Box, Button, Divider, Typography } from "@mui/material";
import _ from "lodash";
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline } from "react-icons/md";
import { useAllCells, useAllUsers } from "../../../queries/hooks";
import { toast } from "react-toastify";
import { object, string } from "yup";

import { useProject } from "../../../contexts/project.context";
import { ProjectGroup } from "../../../types";
import { Group, User } from "../../../types/user.type";
import { Input } from "../../Input";
import TabContent from "../components/TabContent";

export interface ProjectAssignTimeHandle {
  validate: () => boolean;
}

interface ProjectClientProps {
  onNext: () => void;
  onBack?: () => void;
}

const emptyGroup: ProjectGroup = {
  group: {} as Group,
  leader: {} as User,
  firstCoordinator: {} as User,
  secondCoordinator: {} as User,
};

const schema = object({
  commercial: object({
    _id: string().required("Novos negócios - Comercial é obrigatório"),
    name: string(),
  }).required("Novos negócios - Comercial é obrigatório"),

  firstAttendance: object({
    _id: string().required("Atendimento 1 é obrigatório"),
    name: string(),
  }).required("Atendimento 1 é obrigatório"),
  secondAttendance: object({ _id: string(), name: string() }).nullable(),
});

interface GroupFieldErrors {
  group: boolean;
  leader: boolean;
  firstCoordinator: boolean;
}

const emptyGroupError: GroupFieldErrors = {
  group: false,
  leader: false,
  firstCoordinator: false,
};

const ProjectAssignTime = forwardRef<ProjectAssignTimeHandle, ProjectClientProps>(
  ({ onNext, onBack }: ProjectClientProps, ref) => {
  const { assignTime, updateAssignTime, showNotification } = useProject();
  const [groups, setGroups] = useState<ProjectGroup[]>([emptyGroup]);
  const [commercialError, setCommercialError] = useState(false);
  const [firstAttendanceError, setFirstAttendanceError] = useState(false);
  const [groupErrors, setGroupErrors] = useState<GroupFieldErrors[]>([emptyGroupError]);

  const {
    reset,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      commercial: {},
      firstAttendance: {},
      secondAttendance: {},
    },
    resolver: yupResolver(schema),
    mode: "all",
  });

  const commercialWatch = watch("commercial");
  const firstAttendanceWatch = watch("firstAttendance");
  const secondAttendanceWatch = watch("secondAttendance");

  const { data: groupsData, isLoading: groupsLoading } = useAllCells();

  const { data: usersData, isLoading: usersLoading } = useAllUsers();

  function handleGroup(index: number, group?: ProjectGroup) {
    const newGroups = [...groups];
    newGroups[index] = group ?? ({} as ProjectGroup);
    setGroups(newGroups);

    if (index === 0) {
      const updated = group ?? ({} as ProjectGroup);
      setGroupErrors((prev) => {
        const next = [...prev];
        next[0] = {
          group: prev[0].group && _.isEmpty(updated.group),
          leader: prev[0].leader && _.isEmpty(updated.leader),
          firstCoordinator: prev[0].firstCoordinator && _.isEmpty(updated.firstCoordinator),
        };
        return next;
      });
    }
  }

  function handleGroupDelete(index: number) {
    const newGroups = [...groups];
    newGroups.splice(index, 1);
    setGroups(newGroups);
    setGroupErrors((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }

  function runValidation(): boolean {
    const commercialVal = watch("commercial") as any;
    const firstAttendanceVal = watch("firstAttendance") as any;
    let isValid = true;

    const missingCommercial = !commercialVal?._id;
    const missingFirstAttendance = !firstAttendanceVal?._id;

    setCommercialError(missingCommercial);
    setFirstAttendanceError(missingFirstAttendance);

    if (missingCommercial) {
      toast.error("Novos negócios - Comercial é obrigatório.");
      isValid = false;
    }
    if (missingFirstAttendance) {
      toast.error("Atendimento 1 é obrigatório.");
      isValid = false;
    }

    const g0 = groups[0];
    const g0Errors: GroupFieldErrors = {
      group: _.isEmpty(g0?.group),
      leader: _.isEmpty(g0?.leader),
      firstCoordinator: _.isEmpty(g0?.firstCoordinator),
    };
    setGroupErrors((prev) => {
      const next = [...prev];
      next[0] = g0Errors;
      return next;
    });

    if (g0Errors.group) { toast.error("Selecione uma célula"); isValid = false; }
    if (g0Errors.leader) { toast.error("Selecione um Líder para a célula 1"); isValid = false; }
    if (g0Errors.firstCoordinator) { toast.error("Selecione o Coordenador 1 para a célula"); isValid = false; }

    return isValid;
  }

  useImperativeHandle(ref, () => ({
    validate: () => runValidation(),
  }));

  function onSubmit(form: any) {
    const isValid = runValidation();
    if (!isValid) return;
    updateAssignTime({ ...form, groups });
    onNext();
  }
  useEffect(() => {
    if (assignTime) {
      reset(assignTime);

      if (assignTime.groups.length > 0) {
        setGroups([...assignTime.groups]);
      }
    }
  }, []);
  return (
    <TabContent onSubmit={handleSubmit(onSubmit, () => runValidation())} onBack={onBack}>
      <Box>
        <Box fontSize={"16px"}>
          <strong>Atendimento</strong>
        </Box>
        <Box
          display="flex"
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={2}
        >
          <Autocomplete
            options={usersData || []}
            getOptionLabel={(option) => option.name || ""}
            value={commercialWatch}
            isOptionEqualToValue={(option, value) => option._id === value?._id}
            onChange={(_, user) => {
              setValue("commercial", user);
              clearErrors("commercial");
              setCommercialError(!user?._id);
            }}
            fullWidth
            loading={usersLoading}
            loadingText={"Carregando..."}
            renderInput={(params) => (
              <Input
                placeholder="Clique para selecionar"
                label="Novos negócios -  Comercial *"
                error={commercialError}
                helperText={commercialError ? "Campo Obrigatório" : undefined}
                {...params}
              />
            )}
          />

          <Autocomplete
            fullWidth
            options={usersData || []}
            getOptionLabel={(option) => option.name || ""}
            value={firstAttendanceWatch}
            isOptionEqualToValue={(option, value) => option._id === value?._id}
            onChange={(_, user) => {
              setValue("firstAttendance", user);
              clearErrors("firstAttendance");
              setFirstAttendanceError(!user?._id);
            }}
            loading={usersLoading}
            loadingText={"Carregando..."}
            renderInput={(params) => (
              <Input
                placeholder="Clique para selecionar"
                label="Atendimento 1 *"
                error={firstAttendanceError}
                helperText={firstAttendanceError ? "Campo Obrigatório" : undefined}
                {...params}
              />
            )}
          />

          <Autocomplete
            fullWidth
            options={usersData || []}
            getOptionLabel={(option) => option.name || ""}
            value={secondAttendanceWatch}
            isOptionEqualToValue={(option, value) => option._id === value?._id}
            onChange={(_, user) => {
              setValue("secondAttendance", user),
                clearErrors("secondAttendance");
            }}
            loading={usersLoading}
            loadingText={"Carregando..."}
            renderInput={(params) => (
              <Input
                placeholder="Clique para selecionar"
                label="Atendimento 2"
                {...params}
              />
            )}
          />
        </Box>
      </Box>
      <Box fontSize={"16px"}>
        <strong>Células</strong>
      </Box>
      {groups.map((productGroup, index) => (
        <Box
          border={"1px solid #eee"}
          borderRadius={2}
          padding={1.5}
          key={index}
        >
          {index >= 0 && (
            <Box display={"flex"} alignItems={"center"} gap={1}>
              <Typography variant="body2">Célula {index + 1}</Typography>
            </Box>
          )}
          <Box width={"33%"}>
            <Autocomplete
              fullWidth
              options={groupsData || []}
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, group) => {
                if (group === null) {
                  return handleGroup(index, emptyGroup);
                }
                const leader = usersData?.find(
                  (el) => el._id === group?.leader_user_ids[0]
                );
                handleGroup(index, {
                  ...productGroup,
                  _id: group?._id ?? undefined,
                  group: group ?? ({} as Group),
                  leader: leader ?? ({} as User),
                  firstCoordinator: {} as User,
                  secondCoordinator: {} as User,
                });
              }}
              value={productGroup.group}
              isOptionEqualToValue={(option, value) =>
                option._id === value?._id
              }
              loading={groupsLoading}
              loadingText={"Carregando..."}
              renderInput={(params) => (
                <Input
                  placeholder="Clique para selecionar"
                  label={`Grupo`}
                  error={index === 0 && groupErrors[0]?.group}
                  helperText={index === 0 && groupErrors[0]?.group ? "Campo Obrigatório" : undefined}
                  {...params}
                />
              )}
            />
          </Box>
          <Box
            gap={2}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Autocomplete
              options={
                usersData?.filter((user) =>
                  productGroup.group?.leader_user_ids?.includes(user._id)
                ) || []
              }
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, user) =>
                handleGroup(index, {
                  ...productGroup,
                  leader: user ?? ({} as User),
                })
              }
              value={productGroup.leader}
              fullWidth
              isOptionEqualToValue={(option, value) =>
                option._id === value?._id
              }
              loading={usersLoading}
              loadingText={"Carregando..."}
              disabled={!productGroup.group?._id}
              renderInput={(params) => (
                <Input
                  placeholder="Clique para selecionar"
                  label={`Líder *`}
                  error={index === 0 && groupErrors[0]?.leader}
                  helperText={index === 0 && groupErrors[0]?.leader ? "Campo Obrigatório" : undefined}
                  {...params}
                />
              )}
            />

            <Autocomplete
              options={
                usersData?.filter((user) =>
                  productGroup.group?.coordinators_user_ids?.includes(user._id)
                ) || []
              }
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, user) =>
                handleGroup(index, {
                  ...productGroup,
                  firstCoordinator: user ?? ({} as User),
                })
              }
              value={productGroup.firstCoordinator}
              fullWidth
              isOptionEqualToValue={(option, value) =>
                option._id === value?._id
              }
              loading={usersLoading}
              loadingText={"Carregando..."}
              disabled={!productGroup.group?._id}
              renderInput={(params) => (
                <Input
                  placeholder="Clique para selecionar"
                  label={`1º Coordenador/Produtor (Responsável)  `}
                  error={index === 0 && groupErrors[0]?.firstCoordinator}
                  helperText={index === 0 && groupErrors[0]?.firstCoordinator ? "Campo Obrigatório" : undefined}
                  {...params}
                />
              )}
            />

            <Autocomplete
              options={
                usersData?.filter((user) =>
                  productGroup.group?.coordinators_user_ids?.includes(user._id)
                ) || []
              }
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, user) =>
                handleGroup(index, {
                  ...productGroup,
                  secondCoordinator: user ?? ({} as User),
                })
              }
              value={productGroup.secondCoordinator}
              fullWidth
              isOptionEqualToValue={(option, value) =>
                option._id === value?._id
              }
              loading={usersLoading}
              loadingText={"Carregando..."}
              disabled={!productGroup.group?._id}
              renderInput={(params) => (
                <Input
                  placeholder="Clique para selecionar"
                  label={`2º Coordenador/Produtor (Responsável)`}
                  {...params}
                />
              )}
            />
          </Box>
          {index > 0 && (
            <Box display={"flex"} marginLeft={"auto"}>
              <Button
                variant="outlined"
                startIcon={<MdDeleteOutline />}
                color="error"
                onClick={() => handleGroupDelete(index)}
                sx={{ width: 150, mt: 1, ml: "auto" }}
              >
                Remover Célula
              </Button>
            </Box>
          )}
        </Box>
      ))}

      <Box display={"flex"} marginLeft={"auto"}>
        <Button
          variant="outlined"
          onClick={() => {
            setGroups([...groups, emptyGroup]);
            setGroupErrors((prev) => [...prev, emptyGroupError]);
          }}
        >
          Adicionar célula
        </Button>
      </Box>
    </TabContent>
  );
});

export default ProjectAssignTime;
